import { ILogger } from 'library/ts/utilities/logger/interfaces/ILogger';
import { LogService } from 'library/ts/utilities/logger/LogService';
import { IAPIAdapter } from 'plugins/tag/plugin_data_service/ts/Application/adapters/abstract/IAPIAdapter';
import { AdapterId } from 'plugins/tag/plugin_data_service/ts/Application/model/enums/AdapterId';
import { PTVContentType } from 'plugins/tag/plugin_data_service/ts/Application/model/enums/PTVContentType';
import { INextItemEndpoint } from 'plugins/tag/plugin_data_service/ts/Application/interfaces/INextItemEndpoint';
import { INextItemFetchParams } from 'plugins/tag/plugin_data_service/ts/Application/interfaces/INextItemFetchParams';
import { IUECFetchParams } from 'plugins/tag/plugin_data_service/ts/Application/interfaces/IUECFetchParams';
import { IUserListEndpoint } from 'plugins/tag/plugin_data_service/ts/Application/interfaces/IUserListEndpoint';
import { IUpNextEndpoint } from 'plugins/tag/plugin_data_service/ts/Application/interfaces/IUpNextEndpoint';
import { IUserListMutationParams } from 'tag/ts/tag/interfaces/api/IUserListMutationParams';
import { IParamsVO } from 'tag/ts/tag/interfaces/params/IParamsVO';
import { PLUTO_API_PATHS } from './PlutoApiPaths';
import { PLUTO_DEFAULT_DEVICE, PLUTO_SERVERS } from './PlutoServers';

/**
 * @copyright © Paramount Streaming
 * @description Pluto API adapter. Raw 1:1 wrapper for Pluto channels, VOD items, VOD episodes,
 * and UEC up-next plus next-item recommendation endpoints.
 * Simple response parsing (.json()) only; feature layer handles stitched path resolution and enrichment.
 */
export class PlutoAdapter implements IAPIAdapter, IUpNextEndpoint, INextItemEndpoint, IUserListEndpoint {
	readonly id = AdapterId.PLUTO;

	readonly #logger: ILogger = LogService.create( { prefixes: [ 'SmartTag:', 'DataService:', 'PlutoAdapter' ] } );

	/**
	 * @description Dispatches to the appropriate fetch method based on content type.
	 * @param {IParamsVO} paramsVO
	 * @returns {Promise<unknown>} Parsed API response
	 */
	async fetchData( paramsVO: IParamsVO ): Promise<unknown> {
		if ( !paramsVO?.STITCHER?.accessToken ) {
			throw new Error( 'paramsVO.STITCHER.accessToken is required for the converged Pluto adapter (API Router access token `at`)' );
		}

		switch ( paramsVO?.STITCHER?.type ) {
			case PTVContentType.CHANNEL:
				return this.fetchChannels( paramsVO );
			case PTVContentType.MOVIE:
				return this.fetchVodItems( paramsVO );
			case PTVContentType.EPISODE:
				return this.fetchVodEpisodes( paramsVO );
			default:
				throw new Error( 'unknown stitcher content type' );
		}
	}

	/**
	 * @description Fetches live channel data. Throws on non-2xx responses.
	 * @param {IParamsVO} paramsVO
	 * @returns {Promise<unknown>} Parsed channels API response
	 */
	async fetchChannels( paramsVO: IParamsVO ): Promise<unknown> {
		const url = this.#buildChannelsApiUrl( paramsVO );
		const response = await this.#makeRequest( url, paramsVO );
		if ( !response.ok ) {
			throw new Error( `Pluto API request failed: ${response.status}` );
		}
		const json = await response.json() as Record<string, unknown>;
		return this.#normalizeChannelsResponse( json );
	}

	/**
	 * Normalizes the converged channels API response to match the legacy shape expected by PTVMetadataResolver:
	 * - Handles both list response (channels array) and single-channel response (/channels/{slug}.json)
	 * - Maps to top-level `data` array with `id` derived from `videoContentId`
	 */
	#normalizeChannelsResponse( json: Record<string, unknown> ): Record<string, unknown> {
		let channels = json.channels as Array<Record<string, unknown>> | undefined;
		if ( !Array.isArray( channels ) ) {
			const single = ( json.data as Record<string, unknown> ) ?? ( json.channel as Record<string, unknown> ) ?? json;
			if ( single && typeof single === 'object' && ( single.videoContentId != null || single.slug != null ) ) {
				channels = [ single ];
			}
		}
		if ( !Array.isArray( channels ) ) {
			return json;
		}
		const data = channels.map( ( channel ) => {
			const videoContentId = channel.videoContentId as string | undefined;
			return {
				...channel,
				id: videoContentId ?? channel.id,
				slug: channel.slug
			};
		} );
		return { ...json, data };
	}

	/**
	 * @description Fetches VOD movie items. Throws on non-2xx responses.
	 * @param {IParamsVO} paramsVO
	 * @returns {Promise<unknown>} Parsed VOD items API response
	 */
	async fetchVodItems( paramsVO: IParamsVO ): Promise<unknown> {
		const url = this.#buildVodItemsApiUrl( paramsVO );
		const response = await this.#makeRequest( url, paramsVO );
		if ( !response.ok ) {
			throw new Error( `Pluto API request failed: ${response.status}` );
		}
		const json = await response.json() as Record<string, unknown>;
		return this.#normalizeMoviesResponse( json );
	}

	/**
	 * @description Fetches VOD episode data. Throws on non-2xx responses.
	 * @param {IParamsVO} paramsVO
	 * @returns {Promise<unknown>} Parsed VOD episodes API response
	 */
	async fetchVodEpisodes( paramsVO: IParamsVO ): Promise<unknown> {
		const url = this.#buildVodEpisodesApiUrl( paramsVO );
		const response = await this.#makeRequest( url, paramsVO );
		if ( !response.ok ) {
			throw new Error( `Pluto API request failed: ${response.status}` );
		}
		const json = await response.json() as Record<string, unknown>;
		return this.#normalizeEpisodesResponse( json );
	}

	/**
	 * Normalizes the converged movies API response to the flat array shape expected by
	 * PlaybackParamsBuilder → PTVMetadataResolver:
	 *   - Extracts items from `movies` (converged) or falls back to `data` / top-level array
	 *   - Synthesizes `_id` from `contentId` / `content_id`
	 */
	#normalizeMoviesResponse( json: Record<string, unknown> ): Array<Record<string, unknown>> {
		let items = json.movies as Array<Record<string, unknown>> | undefined;
		if ( !Array.isArray( items ) ) {
			items = json.data as Array<Record<string, unknown>> | undefined;
		}
		// Single-movie response from /v3.0/{device}/movies/{contentId}.json (e.g. { movie: {...} })
		if ( !Array.isArray( items ) ) {
			const single = ( json.movie ?? json.data ) as Record<string, unknown> | undefined;
			if ( single && typeof single === 'object' && ( single.contentId != null || single.content_id != null ) ) {
				items = [ single ];
			}
		}
		if ( !Array.isArray( items ) ) {
			return [];
		}
		return items.map( ( movie ) => {
			const contentId = ( movie.contentId ?? movie.content_id ) as string | undefined;
			return {
				...movie,
				_id: contentId,
				id: contentId
			};
		} );
	}

	/**
	 * Normalizes the converged episodes/videos API response to the flat array shape expected by
	 * PlaybackParamsBuilder → PTVMetadataResolver:
	 *   - Extracts items from `results.itemList` (converged) or falls back to `data` / top-level array
	 *   - Synthesizes `_id` from `contentId`
	 */
	#normalizeEpisodesResponse( json: Record<string, unknown> ): Array<Record<string, unknown>> {
		const results = json.results as Record<string, unknown> | undefined;
		let items = results?.itemList as Array<Record<string, unknown>> | undefined;
		if ( !Array.isArray( items ) ) {
			items = json.data as Array<Record<string, unknown>> | undefined;
		}
		if ( !Array.isArray( items ) ) {
			return [];
		}
		return items.map( ( episode ) => {
			const contentId = ( episode.contentId ?? episode.content_id ) as string | undefined;
			return {
				...episode,
				_id: contentId,
				id: contentId
			};
		} );
	}

	#getChannelsServer( paramsVO: IParamsVO ): string {
		return paramsVO?.STITCHER?.servers?.channels ?? PLUTO_SERVERS.channels;
	}

	#getVodServer( paramsVO: IParamsVO ): string {
		return paramsVO?.STITCHER?.servers?.vod ?? PLUTO_SERVERS.vod;
	}

	#getUecServer( paramsVO?: IParamsVO ): string {
		return ( paramsVO?.STITCHER?.servers?.uec ?? PLUTO_SERVERS.uec ).replace( /\/$/, '' );
	}

	#getNextItemServer( paramsVO?: IParamsVO ): string {
		return ( paramsVO?.STITCHER?.servers?.nextItem ?? paramsVO?.STITCHER?.servers?.uec ?? PLUTO_SERVERS.nextItem ).replace( /\/$/, '' );
	}

	#getUecPath( paramsVO?: IParamsVO ): string {
		const raw = paramsVO?.STITCHER?.paths?.uec ?? PLUTO_API_PATHS.upNext;
		return raw.startsWith( '/' ) ? raw : `/${raw}`;
	}

	#getNextItemPath( paramsVO?: IParamsVO ): string {
		const raw = paramsVO?.STITCHER?.paths?.nextItem ?? PLUTO_API_PATHS.nextItem;
		return raw.startsWith( '/' ) ? raw : `/${raw}`;
	}

	/**
	 * @description Host for user-list mutations. Reuses the converged UEC host
	 * (`STITCHER.servers.uec`) when set; falls back to the Pluto-direct VOD host so legacy
	 * CTV behavior is preserved. Trailing slash is stripped so callers can safely concatenate
	 * a leading-slash path.
	 *
	 * **Pairing requirement:** when `STITCHER.paths.userListAdd` / `.userListRemove` is set
	 * without `STITCHER.servers.uec`, this falls back to the default Pluto VOD host and the
	 * resulting URL will be misrouted. Consumers configuring path overrides must also set
	 * the host override.
	 *
	 * @param paramsVO - Tag params; source of optional host override
	 * @returns Resolved host with trailing slash stripped
	 */
	#getUserListServer( paramsVO?: IParamsVO ): string {
		return ( paramsVO?.STITCHER?.servers?.uec ?? PLUTO_SERVERS.vod ).replace( /\/$/, '' );
	}

	/**
	 * @description Resolves the path for a user-list mutation. When the consumer-config
	 * override (`STITCHER.paths.userListAdd` / `.userListRemove`) is set, both mutation
	 * methods of the same kind collapse to that single path (TN Web disambiguates by
	 * request-body shape — channel slug vs `contentId` + `contentType`). When unset,
	 * each method falls back to its own Pluto-direct default — watchlist for My List,
	 * favorites for Favorites — preserving CTV legacy distinct-endpoint behavior.
	 *
	 * Leading-slash normalization mirrors `#getUecPath` / `#getNextItemPath`: accepts
	 * either `/api/foo` or `api/foo` from consumer config.
	 *
	 * @param paramsVO - Tag params; source of the optional override path
	 * @param kind - `add` or `remove` — selects which override key to read
	 * @param defaultPath - Pluto-direct fallback when no override is set (e.g. `PLUTO_API_PATHS.watchlistAdd` for My List, `.favoritesAdd` for Favorites)
	 * @returns Resolved path with guaranteed leading slash
	 */
	#resolveUserListPath( paramsVO: IParamsVO | undefined, kind: 'add' | 'remove', defaultPath: string ): string {
		const override = kind === 'add'
			? paramsVO?.STITCHER?.paths?.userListAdd
			: paramsVO?.STITCHER?.paths?.userListRemove;
		const raw = override ?? defaultPath;
		return raw.startsWith( '/' ) ? raw : `/${raw}`;
	}

	/** True when the user-list path is consumer-configured (TN Web proxy); false when using the Pluto-direct default. */
	#hasUserListPathOverride( paramsVO: IParamsVO | undefined, kind: 'add' | 'remove' ): boolean {
		const override = kind === 'add'
			? paramsVO?.STITCHER?.paths?.userListAdd
			: paramsVO?.STITCHER?.paths?.userListRemove;
		return typeof override === 'string' && override.length > 0;
	}

	/**
	 * API Router (apps-api) paths: inserts `/{device}/` after the version segment.
	 * e.g. `/v3.0/live/channels.json` → `/v3.0/web/live/channels.json`
	 */
	#appsApiPath( relativeApiPath: string ): string {
		const device = PLUTO_DEFAULT_DEVICE;
		const suffix = relativeApiPath.startsWith( '/' ) ? relativeApiPath : `/${relativeApiPath}`;
		return suffix.replace( /^(\/v[\d.]+)\//, `$1/${device}/` );
	}

	#buildChannelsApiUrl( paramsVO: IParamsVO ): string {
		const baseUrl = this.#getChannelsServer( paramsVO );
		const channelSlug = paramsVO?.STITCHER?.id ?? '';
		const params = paramsVO?.STITCHER?.stitcherParams ?? '';
		const searchParams = new URLSearchParams( params );
		if ( !searchParams.has( '_clientRegion' ) ) {
			searchParams.set( '_clientRegion', searchParams.get( 'marketingRegion' ) ?? 'US' );
		}
		const qs = searchParams.toString();
		const path = channelSlug
			? `${PLUTO_API_PATHS.channelBySlug}/${encodeURIComponent( channelSlug )}.json`
			: PLUTO_API_PATHS.channels;
		const querySuffix = qs ? `?${qs}` : '';
		return `${baseUrl}${this.#appsApiPath( path )}${querySuffix}`;
	}

	#buildVodItemsApiUrl( paramsVO: IParamsVO ): string {
		const baseUrl = this.#getVodServer( paramsVO );
		const contentId = paramsVO?.STITCHER?.id ?? '';
		const searchParams = new URLSearchParams( paramsVO?.STITCHER?.stitcherParams ?? '' );
		// Converged: /v3.0/{device}/movies/{contentId}.json — device segment is inserted by #appsApiPath
		const path = `${PLUTO_API_PATHS.moviesBase}/movies/${encodeURIComponent( contentId )}.json`;
		const qs = searchParams.toString();
		const querySuffix = qs ? `?${qs}` : '';
		return `${baseUrl}${this.#appsApiPath( path )}${querySuffix}`;
	}

	#buildVodEpisodesApiUrl( paramsVO: IParamsVO ): string {
		const baseUrl = this.#getVodServer( paramsVO );
		const contentId = paramsVO?.STITCHER?.id ?? '';
		const searchParams = new URLSearchParams( paramsVO?.STITCHER?.stitcherParams ?? '' );
		searchParams.set( 'contentId', contentId );
		return `${baseUrl}${this.#appsApiPath( PLUTO_API_PATHS.vodEpisodes )}?${searchParams.toString()}`;
	}

	async fetchUpNextRecommendations( params: IUECFetchParams, paramsVO?: IParamsVO ): Promise<unknown> {
		const url = this.#buildUpNextApiUrl( params, paramsVO );
		this.#logger.log( '[UEC]', 'fetchUpNextRecommendations', { url, params } );
		try {
			const response = await fetch( url, { method: 'GET' } );
			if ( !response.ok ) {
				this.#logger.warn( '[UEC]', 'up-next request failed', { status: response.status, url } );
				throw new Error( `UEC up-next request failed: ${response.status}` );
			}
			return response.json() as unknown;
		} catch ( error ) {
			if ( error instanceof Error && error.message.startsWith( 'UEC up-next request failed' ) ) {
				throw error;
			}
			this.#logger.warn( '[UEC]', 'fetchUpNextRecommendations error', {
				error: ( error as Error )?.message ?? error,
				url
			} );
			throw error;
		}
	}

	#buildUpNextApiUrl( params: IUECFetchParams, paramsVO?: IParamsVO ): string {
		const baseUrl = this.#getUecServer( paramsVO );
		const searchParams = new URLSearchParams();

		searchParams.set( 'endCardId', params.endCardId );
		searchParams.set( 'contentType', params.contentType );

		if ( params._clientRegion ) {
			searchParams.set( '_clientRegion', params._clientRegion );
		}
		if ( params.channelSlug ) {
			searchParams.set( 'channelSlug', params.channelSlug );
		}
		if ( params.device ) {
			searchParams.set( 'device', params.device );
		}
		if ( params.start !== undefined ) {
			searchParams.set( 'start', String( params.start ) );
		}
		if ( params.rows !== undefined ) {
			searchParams.set( 'rows', String( params.rows ) );
		}
		if ( params.nextItem === true ) {
			searchParams.set( 'nextItem', 'true' );
		}

		return `${baseUrl}${this.#getUecPath( paramsVO )}?${searchParams.toString()}`;
	}

	async fetchNextItemRecommendations( params: INextItemFetchParams, paramsVO?: IParamsVO ): Promise<unknown> {
		const url = this.#buildNextItemApiUrl( params, paramsVO );
		try {
			const response = await fetch( url, { method: 'GET' } );
			if ( !response.ok ) {
				this.#logger.warn( '[NextItem]', 'next-item request failed', { status: response.status, url } );
				throw new Error( `Next-item request failed: ${response.status}` );
			}
			return response.json() as unknown;
		} catch ( error ) {
			if ( error instanceof Error && error.message.startsWith( 'Next-item request failed' ) ) {
				throw error;
			}
			this.#logger.warn( '[NextItem]', 'fetchNextItemRecommendations error', {
				error: ( error as Error )?.message ?? error,
				url
			} );
			throw error;
		}
	}

	#buildNextItemApiUrl( params: INextItemFetchParams, paramsVO?: IParamsVO ): string {
		const baseUrl = this.#getNextItemServer( paramsVO );
		const searchParams = new URLSearchParams();

		searchParams.set( 'endCardId', params.endCardId );
		searchParams.set( 'contentType', params.contentType );

		if ( params._clientRegion ) {
			searchParams.set( '_clientRegion', params._clientRegion );
		}
		if ( params.channelSlug ) {
			searchParams.set( 'channelSlug', params.channelSlug );
		}
		if ( params.device ) {
			searchParams.set( 'device', params.device );
		}
		if ( params.start !== undefined ) {
			searchParams.set( 'start', String( params.start ) );
		}
		if ( params.rows !== undefined ) {
			searchParams.set( 'rows', String( params.rows ) );
		}
		if ( params.contentId ) {
			searchParams.set( 'contentId', params.contentId );
		}
		if ( params.episodeId ) {
			searchParams.set( 'episodeId', params.episodeId );
		}
		if ( params.showId ) {
			searchParams.set( 'showId', params.showId );
		}

		return `${baseUrl}${this.#getNextItemPath( paramsVO )}?${searchParams.toString()}`;
	}

	#makeRequest( url: string, paramsVO: IParamsVO ): Promise<Response> {
		const requestUrl = this.#appendAccessToken( url, paramsVO );
		const headers: HeadersInit = { 'Content-Type': 'application/json' };
		const token = paramsVO?.STITCHER?.token;
		if ( token ) {
			headers[ 'Authorization' ] = `Bearer ${token}`;
		}
		return fetch( requestUrl, { method: 'GET', headers } );
	}

	#appendAccessToken( url: string, paramsVO: IParamsVO | undefined ): string {
		const accessToken = paramsVO?.STITCHER?.accessToken;
		if ( !accessToken ) {
			return url;
		}
		const separator = url.includes( '?' ) ? '&' : '?';
		return `${url}${separator}at=${encodeURIComponent( accessToken )}`;
	}

	/**
	 * @description My List / Favorites mutations — Live channels and VOD. Host + path resolve
	 * from `paramsVO.STITCHER` overrides when set (TN Web proxy), otherwise fall back to
	 * Pluto-direct apps-api defaults — distinct `watchlist` vs `favorites` endpoints, preserving
	 * CTV legacy behavior. When an override is set, both add methods (`addToMyList` +
	 * `addToFavorites`) collapse to `STITCHER.paths.userListAdd` and both remove methods
	 * collapse to `STITCHER.paths.userListRemove`; TN Web disambiguates watchlist vs favorite
	 * (and **Live channel vs VOD**) by request-body shape on its side — see
	 * `#buildUserListBody` for the channel-vs-VOD body selection rules.
	 *
	 * Auth: `paramsVO.STITCHER.accessToken` (`?at=`) is appended when present (CTV) — Web has
	 * no token and routes through the TN Web proxy's session cookie instead.
	 */
	async addToMyList( params: IUserListMutationParams, paramsVO?: IParamsVO ): Promise<unknown> {
		return this.#postUserListMutation( this.#resolveUserListPath( paramsVO, 'add', PLUTO_API_PATHS.watchlistAdd ), params, paramsVO, 'add' );
	}

	async removeFromMyList( params: IUserListMutationParams, paramsVO?: IParamsVO ): Promise<unknown> {
		return this.#postUserListMutation( this.#resolveUserListPath( paramsVO, 'remove', PLUTO_API_PATHS.watchlistRemove ), params, paramsVO, 'remove' );
	}

	async addToFavorites( params: IUserListMutationParams, paramsVO?: IParamsVO ): Promise<unknown> {
		return this.#postUserListMutation( this.#resolveUserListPath( paramsVO, 'add', PLUTO_API_PATHS.favoritesAdd ), params, paramsVO, 'add' );
	}

	async removeFromFavorites( params: IUserListMutationParams, paramsVO?: IParamsVO ): Promise<unknown> {
		return this.#postUserListMutation( this.#resolveUserListPath( paramsVO, 'remove', PLUTO_API_PATHS.favoritesRemove ), params, paramsVO, 'remove' );
	}

	/**
	 * @description Builds the user-list mutation request body. The body shape selects the
	 * **Live vs VOD** routing on TN Web's side (Pluto-direct legacy uses the same body):
	 *
	 * * **Live channel** — `params.channelSlug` set → body `{ channelSlug }`. Used for Pluto
	 *   live linear channels (EPG / player overlay favorite). TN Web routes channel-shaped
	 *   bodies to the channel-favorites upstream.
	 * * **VOD** — `params.contentId` set → body `{ contentId, contentType? }`. Used for shows
	 *   / movies (endcard CTA, My List). TN Web routes contentId-shaped bodies to the VOD
	 *   watchlist/favorites upstream.
	 *
	 * `channelSlug` wins when both are present. Throws when neither identifier is supplied.
	 * Field names match Pluto's apps-api / TN Web proxy contract verbatim.
	 *
	 * @param params - Mutation payload from the consumer
	 * @returns POST body shaped for the platform watchlist / favorites API
	 */
	#buildUserListBody( params: IUserListMutationParams ): Record<string, unknown> {
		// Live channel mutation — TN Web routes channel-shaped bodies to the channel-favorites upstream.
		if ( params.channelSlug ) {
			return { channelSlug: params.channelSlug };
		}
		// VOD mutation — TN Web routes contentId-shaped bodies to the VOD watchlist/favorites upstream.
		if ( !params.contentId ) {
			throw new Error( 'PlutoAdapter: either channelSlug (live channel) or contentId (VOD) is required for user list mutation' );
		}
		const body: Record<string, unknown> = { contentId: params.contentId };
		if ( params.contentType ) {
			body.contentType = params.contentType;
		}
		return body;
	}

	/**
	 * @description Executes a user-list POST mutation. Pre-resolves the path via
	 * `#resolveUserListPath` (host + override-collapse handled in the caller), then:
	 *
	 * - Injects the apps-api `/{device}/` segment **only when no path override is set**
	 *   (TN Web proxy paths are device-agnostic; Pluto-direct legacy paths need it).
	 * - Appends `?at=<accessToken>` via `#appendAccessToken` when `paramsVO.STITCHER.accessToken`
	 *   is present (CTV); no-op when absent (Web — TN Web proxy authenticates via session cookie).
	 *
	 * Body shape is selected by which `params` field is provided: `channelSlug` → `{ channelSlug }`
	 * (channel mutation); else `contentId` → `{ contentId, contentType? }` (VOD mutation). TN Web
	 * proxies disambiguate watchlist vs favorite (and channel vs VOD) server-side based on this
	 * body shape; Pluto-direct legacy hits the same endpoint family with the same body.
	 *
	 * @param relativePath - Pre-resolved path with leading slash (from `#resolveUserListPath`)
	 * @param params - Mutation payload; must include either `channelSlug` (channel mutation) or `contentId` (VOD mutation; `contentType` optional)
	 * @param paramsVO - Tag params; source of host, access token, and path-override signal
	 * @param kind - `add` or `remove` — used by `#hasUserListPathOverride` to decide device-segment injection
	 * @returns Parsed JSON response body, or `{}` for empty / non-JSON 2xx responses
	 */
	async #postUserListMutation( relativePath: string, params: IUserListMutationParams, paramsVO: IParamsVO | undefined, kind: 'add' | 'remove' ): Promise<unknown> {
		const body = this.#buildUserListBody( params );
		// Override paths (TN Web proxy) are device-agnostic; only Pluto-direct apps-api paths
		// need the `/{device}/` segment injection.
		const hasOverride = this.#hasUserListPathOverride( paramsVO, kind );
		const resolvedPath = hasOverride ? relativePath : this.#appsApiPath( relativePath );
		const baseUrl = `${this.#getUserListServer( paramsVO )}${resolvedPath}`;
		// `?at=<accessToken>` is appended on CTV (present); no-op on Web (absent) — proxy
		// authenticates via the user's TN Web session cookie instead.
		const requestUrl = this.#appendAccessToken( baseUrl, paramsVO );
		const response = await fetch( requestUrl, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify( body )
		} );
		if ( !response.ok ) {
			throw new Error( `Pluto user list mutation failed: ${response.status}` );
		}
		const text = await response.text();
		if ( !text?.trim() ) {
			return {};
		}
		try {
			return JSON.parse( text ) as unknown;
		} catch {
			return {};
		}
	}
}